import { useLayoutEffect, useRef, useState } from "react";
import { NavigationType, useLocation, useNavigationType, type Location } from "react-router";

/**
 * Переход между экранами (FE-13).
 *
 * Приложение живёт на декларативном `<BrowserRouter>` (см. комментарий в
 * App.tsx — иначе теряются параметры запуска Telegram во фрагменте URL), а
 * встроенная в роутер поддержка View Transitions существует только для
 * data-роутера. Поэтому переход собран здесь руками, тем же приёмом:
 *
 *  1. Хук отдаёт НЕ текущую локацию, а показываемую. `<Routes location={…}>`
 *     держит на экране старый экран, пока браузер снимает с него слепок.
 *  2. Внутри `startViewTransition` показываемая локация меняется на текущую.
 *     Колбэк возвращает промис, который резолвится в layout-эффекте после
 *     коммита нового экрана, — так обходимся без `flushSync`, который в
 *     эффекте React встречает предупреждением.
 *  3. Направление кладётся на `<html data-nav>`: `forward` для PUSH,
 *     `back` для POP. Аппаратная кнопка «Назад» Telegram ходит через
 *     `navigate(-1)` и попадает в POP сама.
 *
 * Где View Transitions нет (старые WebView), показываемая локация меняется
 * сразу, а вход нового экрана отрабатывает CSS-анимацией `page-enter`.
 * Направление при этом сохраняется — фолбэк отличается только тем, что
 * уходящий экран не анимируется.
 */
export function supportsViewTransition(): boolean {
  return typeof document !== "undefined" && typeof document.startViewTransition === "function";
}

export function useRouteTransition(): Location {
  const location = useLocation();
  const navigationType = useNavigationType();
  const [displayed, setDisplayed] = useState(location);

  // Резолвер колбэка startViewTransition: вызывается, когда новый экран
  // действительно оказался в DOM.
  const commit = useRef<(() => void) | undefined>(undefined);

  useLayoutEffect(() => {
    commit.current?.();
    commit.current = undefined;
  }, [displayed]);

  useLayoutEffect(() => {
    if (displayed.key === location.key) return;

    const root = document.documentElement;
    root.dataset.nav = navigationType === NavigationType.Pop ? "back" : "forward";

    if (!supportsViewTransition()) {
      setDisplayed(location);
      return;
    }

    const transition = document.startViewTransition(
      () =>
        new Promise<void>((resolve) => {
          commit.current = resolve;
          setDisplayed(location);
        }),
    );

    void transition.finished.catch(() => undefined);
  }, [displayed, location, navigationType]);

  return displayed;
}
